import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { useSettings } from "@/context/SettingsContext";

const FALLBACK_CHAPTERS = [
  {
    title: "Itineraries tailored to the discerning traveller",
    desc: "No off-the-shelf packages. Every route, hotel and table is chosen around your pace, palate and curiosity.",
  },
  {
    title: "A 99.4% visa approval track record",
    desc: "Dedicated visa desks for Schengen, USA, UAE and 40+ countries — documentation audited twice before filing.",
  },
  {
    title: "Private chauffeurs & heritage stays",
    desc: "Hand-picked 5-star and boutique heritage properties, with drivers who double up as local storytellers.",
  },
  {
    title: "Concierge access, before to beyond",
    desc: "One direct line to your travel designer — before you book, while you travel, and after you return.",
  },
];

const Counter = ({ to, suffix }) => {
  const ref = useRef(null);

  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2.2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} className="font-display text-3xl sm:text-4xl text-gold">
      {val.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
};

export const ManifestoChapters = () => {
  const settings = useSettings();
  const sinceYear = new Date().getFullYear() - (Number(settings.stat_years) || 30);
  const chapters = [1, 2, 3, 4].map((n) => ({
    num: `0${n}`,
    title: settings[`why_${n}_title`] || FALLBACK_CHAPTERS[n - 1].title,
    desc: settings[`why_${n}_desc`] || FALLBACK_CHAPTERS[n - 1].desc,
  }));
  const stats = [
    { to: Number(settings.stat_years) || 0, suffix: "+", label: "Years of Craft" },
    { to: Number(settings.stat_countries) || 0, suffix: "+", label: "Countries Served" },
    { to: Number(settings.stat_travellers) || 0, suffix: "+", label: "Happy Travellers" },
    { to: Number(settings.stat_experts) || 0, suffix: "+", label: "Travel Experts" },
  ];

  return (
    <section id="why" data-testid="manifesto-section" className="py-20 md:py-28 px-4 sm:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
            >
              <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-4">02 — Why {settings.company_name || "Travelrushh International Tourism"}</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-cream mb-6">
                Travel, treated as <span className="italic text-gold">a craft.</span>
              </h2>
              <p className="text-base text-mist leading-relaxed max-w-md">
                Since {sinceYear}, we've believed a holiday should feel hand-made — measured in moments,
                not milestones. Four promises sit behind every journey we design.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-10 mt-12">
              {stats.map((s) => (
                <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Counter to={s.to} suffix={s.suffix} />
                  <p className="text-xs tracking-[0.2em] uppercase text-faded mt-2">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col">
          {chapters.map((c, i) => (
            <motion.div
              key={c.num}
              data-testid={`manifesto-chapter-${c.num}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="group border-t border-cream/10 py-10 first:border-t-0 first:pt-0 lg:first:pt-10 lg:first:border-t"
            >
              <div className="flex items-start gap-6 sm:gap-10">
                <span className="font-mono text-sm text-gold/70 pt-2 group-hover:text-gold transition-colors duration-300">
                  {c.num}
                </span>
                <div>
                  <h3 className="font-display text-2xl sm:text-3xl text-cream mb-3 group-hover:text-gold transition-colors duration-300">
                    {c.title}
                  </h3>
                  <p className="text-sm sm:text-base text-mist leading-relaxed max-w-xl">{c.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
